import { useNavigate, useParams } from "react-router-dom";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { UIInput } from "ui";
import { ButtonCustom } from "../../components/auth/styles";
import { Trans } from "react-i18next";
import "./RecoverPasswordForm.sass";
import { t } from "i18next";
import { ROUTE_URLS } from "../../constants/consts";
import Swal from "sweetalert2";
import { recoverPassword, validateToken } from "../../api/userPasswordReset";

export const RecoverPasswordForm = () => {
  const { recoverToken } = useParams();
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const navigate = useNavigate();

  const optionsForAlertSuccess: any = useMemo(() => ({
    title: t("recover_password_form.password_changed_title"),
    text: "",
    icon: "success",
    confirmButtonText: t("confirmButton"),
    confirmButtonColor: "#0ebfff",
  }), []);

  const optionsForAlertWithInvalidToken: any = useMemo(() => ({
    title: t("recover_password_form.access_denied_title"),
    text: t("recover_password_form.access_denied_message"),
    icon: "error",
    confirmButtonText: t("confirmButton"),
    confirmButtonColor: "#0ebfff",
  }), []);

  const optionsForAlertWithInvalidCred: any = useMemo(() => ({
    title: t("recover_password_form.user_not_found_title"),
    text: t("recover_password_form.user_not_found_message"),
    icon: "error",
    confirmButtonText: t("confirmButton"),
    confirmButtonColor: "#0ebfff",
  }), []);

  const validateAuthToken = useCallback(async (token: string) => {
    const validateResponse = await validateToken(token);
    if (validateResponse.error) {
      navigate(ROUTE_URLS.SIGN_IN);
      Swal.fire(optionsForAlertWithInvalidToken).then(() => {});
      return;
    }
    if (validateResponse?.data?.data) {
      setEmail(validateResponse?.data?.data?.email || "");
    }
  }, [navigate, optionsForAlertWithInvalidToken]);

  useEffect(() => {
    if (!recoverToken) return;
    validateAuthToken(recoverToken);
  }, [recoverToken, validateAuthToken]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const data = {
        email: email?.trim() || "",
        password: newPassword?.trim() || "",
      };
      if (!recoverToken || !newPassword) return;
      const response = await recoverPassword(
        JSON.stringify(data),
        recoverToken
      );

      if (response.data.data) {
        navigate(ROUTE_URLS.HOME);
        Swal.fire(optionsForAlertSuccess).then(() => {});
      } else {
        Swal.fire(optionsForAlertWithInvalidCred).then(() => {});
      }
      navigate(ROUTE_URLS.SIGN_IN);
      Swal.fire(optionsForAlertSuccess).then(() => {});
    } catch (error) {
      Swal.fire(optionsForAlertWithInvalidCred).then(() => {});
    }
  };

  const textFieldStyles = {
    transition: "width 0.3s ease-in-out",
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#ebebeb",
      padding: "0 10px",
      "& fieldset": {
        borderColor: "transparent",
      },
      "&:hover fieldset": {
        border: "none",
        borderColor: "gray",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
    },
  };

  const inputPropsStyles = {
    "&::placeholder": {
      color: "#757575",
      fontWeight: "bold",
    },
    "& .MuiInputBase-input:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px #ebebeb inset",
      WebkitTextFillColor: "#000000",
      transition: "background-color 5000s ease-in-out 0s",
    },
    "& .MuiInputBase-input": {
      color: "#000000",
      fontWeight: "bold",
    },
  };

  const inputLabelPropsStyles = {
    color: "#757575",
    fontWeight: "bold",
    "&.Mui-focused": {
      color: "#757575",
    },
  };

  return (
    <div>
      <form className={"recover_form"} onSubmit={handleSubmit}>
        <p className={"help_text"}>
          <Trans
            i18nKey="recover_password_form.new_password_instructions"
            values={{ email }}
          />
        </p>
        <UIInput
          required
          type="code"
          className={"recover_input"}
          label={t("recover_password_form.new_password_label")}
          autoComplete="off"
          InputProps={{
            sx: inputPropsStyles,
          }}
          InputLabelProps={{
            sx: inputLabelPropsStyles,
          }}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value?.trim() || "")}
          sx={textFieldStyles}
          style={{ marginBottom: "15px" }}
        />
        <ButtonCustom type="submit">
          <Trans i18nKey="recover_password_form.update_password_button" />
        </ButtonCustom>
      </form>
    </div>
  );
};
